import { FC, useEffect, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, Typography, SvgIcon } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { client, MarketSupplier, MarketSupplierFilterOptions, Instance, Market } from '../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../components/forms/Pagination';
import { Section } from '../../../components/ui/Section';
import SuppliersList from './SuppliersList';

type Props = {
  onCreateNew: () => void;
};

const SuppliersTab: FC<Props> = ({ onCreateNew }) => {
  const { t } = useTranslation('suppliers');
  const [list, setList] = useState<MarketSupplier[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const pageSize = 20;

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchMarkets = async () => {
    try {
      const result = await client.getMarkets({
        companyId: selectedInstance || undefined,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setMarkets(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const fetchSuppliers = async () => {
    setIsLoading(true);
    try {
      const options: MarketSupplierFilterOptions = {
        companyId: selectedInstance || undefined,
        customerTypeId: selectedMarket || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
        sort: 'supplierName',
        direction: 'asc',
      };
      const result = await client.getMarketSuppliers(options);
      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      } else {
        setList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
  }, []);

  useEffect(() => {
    if (selectedInstance) {
      fetchMarkets();
    } else {
      setMarkets([]);
      setSelectedMarket('');
    }
  }, [selectedInstance]);

  useEffect(() => {
    fetchSuppliers();
  }, [currentPage, searchTerm, selectedInstance, selectedMarket]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket(''); // Reset market when instance changes
    setCurrentPage(0);
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
    setCurrentPage(0);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('');
    setSelectedMarket('');
    setCurrentPage(0);
  };

  return (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          <Typography>{t('clear')}</Typography>
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          <Typography>{t('search')}</Typography>
        </Button>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByInstance')}
          </InputLabel>
          <Select
            value={selectedInstance}
            onChange={handleInstanceChange}
            label={t('filterByInstance')}
            displayEmpty
            notched
          >
            <MenuItem value="">
              {t('allInstances')}
            </MenuItem>
            {instances.map((instance) => (
              <MenuItem 
                key={instance.id} 
                value={instance.id.toString()}
              >
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByMarket')}
          </InputLabel>
          <Select
            value={selectedMarket}
            onChange={handleMarketChange}
            label={t('filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            <MenuItem value="">
              {t('allMarkets')}
            </MenuItem>
            {markets.map((market) => (
              <MenuItem 
                key={market.customerTypeId} 
                value={market.customerTypeId.toString()}
              >
                {market.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <SuppliersList
            list={list}
            isLoading={isLoading}
            totalCount={totalCount}
            onClear={handleClear}
            onCreateNew={onCreateNew}
          />
          {totalCount > 0 && (
            <CustomPagination
              page={currentPage}
              setPage={handlePageChange}
              maxPages={Math.ceil(totalCount / pageSize)}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          )}
        </Section>
      </Grid>
    </Grid>
  );
};

export default SuppliersTab;
